import { Inject, Injectable, OnDestroy } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { combineLatest, fromEvent, merge, Observable, of, Subscription, switchMap, throttleTime, timer } from 'rxjs';

import * as Tokens from '@shared/core/tokens';
import * as actions from '@shared/state/actions';
import * as selectors from '@shared/state/selectors';
import { ModalsService } from './modals.shared.service';

@Injectable({
    providedIn: 'root',
})
export class ClearAfterIdleService implements OnDestroy {
    private _triggerMode: OLO.Enums.CLEAR_ON_IDLE_TRIGGER_MODE;
    private _targetUserType: OLO.Enums.CLEAR_ON_IDLE_USER_TYPE;
    private _timeoutInMiliseconds: number;
    private _catchEventInterval: number = 1000;
    private _userEventTypes = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
    private _configClearOnIdle: OLO.Config.Cart.ClearOnIdle;

    private _idleTimer$: Observable<number>;
    private _userEvents$: Observable<Event>[] = [];
    private _isCartEmpty$: Observable<boolean>;
    private _isAuthorized$: Observable<boolean>;
    private _triggersSubscription: Subscription;

    constructor(private _store: Store, private _modalService: ModalsService, @Inject(Tokens.CONFIG_TOKEN) private _config: OLO.Config) {
        this._configClearOnIdle = this._config.cart.clearOnIdle;
        this._timeoutInMiliseconds = this._configClearOnIdle.timeoutInMinutes * 60 * 1000;
        this._triggerMode = this._configClearOnIdle.triggerMode;
        this._idleTimer$ = timer(this._timeoutInMiliseconds, 1000);
        this._targetUserType = this._configClearOnIdle.targetUserType;
        this._isAuthorized$ = this._store.pipe(select(selectors.isMemberAuthorizedJWT));
        this._isCartEmpty$ = this._store.pipe(select(selectors.isCartEmpty));
    }

    public clearAfterIdle(): void {
        this._triggersSubscription = combineLatest([this._isCartEmpty$, this._isAuthorized$])
            .pipe(
                switchMap(([isCartEmpty, isAuthorized]) => {
                    switch (this._targetUserType) {
                        case OLO.Enums.CLEAR_ON_IDLE_USER_TYPE.MEMBER:
                            return !isCartEmpty && isAuthorized ? this._logUserEvents$() : this._stopLoggingUserEvents$();

                        case OLO.Enums.CLEAR_ON_IDLE_USER_TYPE.GUEST:
                            return !isCartEmpty && !isAuthorized ? this._logUserEvents$() : this._stopLoggingUserEvents$();

                        case OLO.Enums.CLEAR_ON_IDLE_USER_TYPE.BOTH:
                            return !isCartEmpty ? this._logUserEvents$() : this._stopLoggingUserEvents$();
                    }
                }),
                switchMap((userEvents) => {
                    if (!userEvents) return this._stopLoggingUserEvents$();

                    return this._idleTimer$;
                }),
            )
            .subscribe((idleTimer) => {
                if (idleTimer !== null) this._handleTimeout();
            });
    }

    private _logUserEvents$(): Observable<Event | null> {
        this._userEvents$ = [];
        this._userEventTypes.forEach((eventType) => this._userEvents$.push(fromEvent(document, eventType)));
        this._userEvents$.push(of(new Event('click')));

        return merge(...this._userEvents$).pipe(throttleTime(this._catchEventInterval));
    }

    private _stopLoggingUserEvents$(): Observable<null> {
        return of(null);
    }

    private _handleTimeout(): void {
        switch (this._triggerMode) {
            case OLO.Enums.CLEAR_ON_IDLE_TRIGGER_MODE.CLEAR_ON_TIMEOUT:
                this._store.dispatch(actions.CartReset());

                break;

            case OLO.Enums.CLEAR_ON_IDLE_TRIGGER_MODE.ASK_CLEAR_ON_TIMEOUT:
                this._modalService.show({
                    type: 'inactive-user-cart-clear',
                });

                break;
        }
    }

    ngOnDestroy(): void {
        if (this._triggersSubscription) {
            this._triggersSubscription.unsubscribe();
        }
    }
}
